import { Box, IconButton } from '@mui/material';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import {
  addMonthsIso,
  formatDayLabel,
  formatMonthLabel,
  isSameMonth,
  monthGrid,
  startOfMonthIso,
  weekdayLabels,
} from './calendarModel';

import { MaterialSymbol } from '@/components/MaterialSymbol';
import { todayIso } from '@/lib';

/**
 * The month calendar behind every date field.
 *
 * It exists because a native `<input type="date">` picker is browser chrome:
 * its panel cannot be styled at all, so it arrives in Chrome's own greys and
 * square corners next to our rounded, tokenised surfaces. The native input is
 * still the *field* (locale segments, no invalid date can be typed) — this
 * replaces only the panel it used to pop up.
 *
 * The look follows the webapp's picker, which themes MUI's calendar to blue-10
 * hover / blue-80 selected / blue-100 on a selected hover, with a 2px ring on
 * today (`Web/src/assets/themes/components/MuiPickersDay.ts`). MUI X is not a
 * dependency here — it is Pro in the webapp, and its free tier still needs a
 * date adapter that would reintroduce local-time `Date` parsing this repo
 * deliberately avoids (`lib/epochDay.ts`) — so the grid is ours, built from
 * `calendarModel`'s UTC arithmetic.
 *
 * The panel is deliberately NOT a focus trap: the field that opened it keeps
 * the keyboard (see `InlineDateCell`), routes the arrow keys here and draws
 * the result through `focusedDate`. Days are still real buttons, so a pointer
 * and a screen reader both get the normal thing.
 */

const DAY_SIZE_PX = 32;

export type CalendarProps = {
  /** The selected day, ISO `YYYY-MM-DD`, or empty for none. */
  value: string;
  /** The month on show, ISO — any day within it. */
  month: string;
  onMonthChange: (monthIso: string) => void;
  onSelect: (isoDate: string) => void;
  /**
   * The day the keyboard is on, ISO, or empty for none.
   *
   * Drawn as a ring, because the panel never holds DOM focus: the field keeps
   * it the whole time (its blur is what commits), so the arrow keys arrive at
   * the input and are routed here. That makes this the only way a keyboard
   * user can see where they are.
   */
  focusedDate?: string;
  automationId?: string;
};

export const Calendar = ({
  value,
  month,
  onMonthChange,
  onSelect,
  focusedDate = '',
  automationId = 'calendar',
}: CalendarProps) => {
  const { t, i18n } = useTranslation();
  const weeks = useMemo(() => monthGrid(month), [month]);
  // Keyed on the language: the day names follow it (ADR 0049).
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const weekdays = useMemo(() => weekdayLabels(), [i18n.language]);
  const today = todayIso();

  return (
    <Box
      data-automation-id={automationId}
      sx={{ p: 1, width: DAY_SIZE_PX * 7 + 16, userSelect: 'none' }}
    >
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          mb: 0.5,
        }}
      >
        <IconButton
          size="tiny"
          aria-label={t('calendar.PreviousMonth')}
          data-automation-id={`${automationId}-prev`}
          onClick={() => onMonthChange(addMonthsIso(startOfMonthIso(month), -1))}
          sx={{ color: 'var(--fg-2)', borderRadius: 'var(--border-radius-medium)' }}
        >
          <MaterialSymbol name="chevron_left" size={18} />
        </IconButton>
        <Box
          data-automation-id={`${automationId}-month`}
          sx={{ fontSize: 14, fontWeight: 600, color: 'var(--fg-heading)' }}
        >
          {formatMonthLabel(month)}
        </Box>
        <IconButton
          size="tiny"
          aria-label={t('calendar.NextMonth')}
          data-automation-id={`${automationId}-next`}
          onClick={() => onMonthChange(addMonthsIso(startOfMonthIso(month), 1))}
          sx={{ color: 'var(--fg-2)', borderRadius: 'var(--border-radius-medium)' }}
        >
          <MaterialSymbol name="chevron_right" size={18} />
        </IconButton>
      </Box>

      <Box sx={{ display: 'grid', gridTemplateColumns: `repeat(7, ${DAY_SIZE_PX}px)` }}>
        {weekdays.map((label, index) => (
          <Box
            // Narrow weekday names repeat ('T' twice), so the index is the key.
            key={index}
            sx={{
              height: DAY_SIZE_PX,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: 11,
              fontWeight: 500,
              color: 'var(--fg-3)',
            }}
          >
            {label}
          </Box>
        ))}

        {weeks.flat().map((iso) => {
          const selected = iso === value;
          const outside = !isSameMonth(iso, month);
          const keyboardHere = iso === focusedDate;
          return (
            <Box
              component="button"
              type="button"
              key={iso}
              data-automation-id={`${automationId}-day`}
              data-date={iso}
              data-selected={selected ? 'true' : undefined}
              data-focused={keyboardHere ? 'true' : undefined}
              aria-label={formatDayLabel(iso)}
              aria-current={iso === today ? 'date' : undefined}
              aria-pressed={selected}
              onClick={() => onSelect(iso)}
              sx={{
                height: DAY_SIZE_PX,
                width: DAY_SIZE_PX,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                p: 0,
                cursor: 'pointer',
                fontFamily: 'var(--font-family)',
                fontSize: 14,
                fontWeight: selected ? 600 : 400,
                // Round cells, like the webapp's — the softest corner there is.
                borderRadius: 'var(--border-radius-pill)',
                border: 'var(--border-width-thin) solid transparent',
                background: selected ? 'var(--color-blue-80)' : 'transparent',
                color: selected
                  ? 'var(--color-black-00)'
                  : outside
                    ? 'var(--fg-disabled)'
                    : 'var(--fg-1)',
                // Today reads as a ring, so it survives being outside the month
                // and never competes with the selected fill.
                ...(iso === today && !selected
                  ? { borderColor: 'var(--color-blue-80)', fontWeight: 600 }
                  : {}),
                /*
                  Where the keyboard is. A filled tint rather than a second
                  ring, so it cannot be mistaken for today's — and it has to
                  read on the selected day too, which is already blue-filled.
                */
                ...(keyboardHere
                  ? {
                      outline: 'var(--focus-ring)',
                      outlineOffset: -1,
                      ...(selected ? {} : { background: 'var(--color-blue-10)' }),
                    }
                  : {}),
                '&:hover': {
                  background: selected ? 'var(--color-blue-100)' : 'var(--color-blue-10)',
                },
                '&:focus-visible': { outline: 'var(--focus-ring)', outlineOffset: -1 },
              }}
            >
              {Number(iso.slice(8, 10))}
            </Box>
          );
        })}
      </Box>
    </Box>
  );
};
